import React, { useState, useEffect } from 'react';
import { createOfferWithMedia } from '../services/api';
import { updateOfferWithMedia } from '../services/offerService';
import MediaUpload from './MediaUpload';
import MediaViewer from './MediaViewer';
import { File as FileIcon, Video, Play, ChevronDown, ChevronUp, CalendarIcon, DollarSign, MessageSquare, PaperclipIcon, BadgeDollarSign, CircleDollarSign } from 'lucide-react';
import '../styles/forms.css';
import { Textarea } from './ui/textarea';
import MediaUploadForWill from './MediaUploadForWill';

interface OfferFormProps {
  willId: string;
  offerId?: string;
  isEditMode?: boolean;
  initialData?: {
    description: string;
    price: string | number;
    currency: string;
    estimatedCompletionDate: string;
    providerContactInfo: string;
  };
  existingMedia?: any[];
  onSuccess?: (offer: any) => void;
  onCancel?: () => void;
  willMedia?: any[];
  defaultContactInfo?: string;
  onSubmit?: (formData: any) => Promise<void>;
}

const OfferForm: React.FC<OfferFormProps> = ({ 
  willId, 
  offerId,
  isEditMode = false,
  initialData,
  existingMedia = [],
  onSuccess, 
  onCancel, 
  willMedia = [],
  defaultContactInfo = '',
  onSubmit 
}) => {
  // Parse price properly if it comes in as a number or string with non-numeric characters
  const formatInitialPrice = (price: string | number | undefined) => {
    if (price === undefined || price === null) return '';
    
    try {
      // Handle numeric prices
      if (typeof price === 'number') {
        return price.toString();
      }
      
      // Handle string prices, possibly with currency symbols
      if (typeof price === 'string') {
        if (price === '') return '';
        
        // Remove currency symbols and non-numeric characters except decimal point
        const cleanedPrice = price.replace(/[^\d.]/g, '');
        
        // Check if it's a valid number after cleaning
        if (cleanedPrice && !isNaN(parseFloat(cleanedPrice))) {
          return parseFloat(cleanedPrice).toString();
        }
      }
      
      return '';
    } catch (error) {
      console.error('Error formatting price:', error);
      return '';
    }
  };

  const [description, setDescription] = useState(initialData?.description || '');
  const [price, setPrice] = useState(formatInitialPrice(initialData?.price) || '');
  const [currency, setCurrency] = useState(initialData?.currency || 'ILS');
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState(initialData?.estimatedCompletionDate || '');
  const [providerContactInfo, setProviderContactInfo] = useState(initialData?.providerContactInfo || defaultContactInfo);
  const [media, setMedia] = useState<any[]>(existingMedia || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  
  // State for collapsible sections
  const hasPriceValue = initialData?.price !== undefined && initialData?.price !== null && initialData?.price !== '';
  const [showPriceSection, setShowPriceSection] = useState(hasPriceValue);
  const [showDateSection, setShowDateSection] = useState(!!initialData?.estimatedCompletionDate);
  const [showContactSection, setShowContactSection] = useState(!!initialData?.providerContactInfo || !!defaultContactInfo);
  const [showAttachmentsSection, setShowAttachmentsSection] = useState(false);

  // For debugging - log initial values
  useEffect(() => {
    console.log('Initial price value:', initialData?.price);
    console.log('Formatted price value:', formatInitialPrice(initialData?.price));
    console.log('Price state value:', price);
    console.log('ShowPriceSection initial state:', showPriceSection);
  }, []);

  // Effect to log price-related information for debugging
  useEffect(() => {
    if (initialData) {
      const rawPrice = initialData.price;
      const formattedPrice = formatInitialPrice(rawPrice);
      console.log('Raw price from initialData:', rawPrice);
      console.log('Type of raw price:', typeof rawPrice);
      console.log('Formatted price:', formattedPrice);
    }
  }, [initialData]);

  // Update form when initialData changes (e.g., when AI suggestion is loaded)
  useEffect(() => {
    if (initialData) {
      console.log('Received initialData:', initialData);
      
      // Safely handle description - ensure it's a string
      if (typeof initialData.description === 'string') {
        setDescription(initialData.description);
      } else if (initialData.description) {
        // If it's an object, try to stringify it
        try {
          setDescription(JSON.stringify(initialData.description));
        } catch (e) {
          console.error('Failed to stringify description:', e);
          setDescription('');
        }
      } else {
        setDescription('');
      }
      
      // Handle price field - could be number or string with currency symbols
      const formattedPrice = formatInitialPrice(initialData.price);
      console.log('Formatted price to set:', formattedPrice);
      setPrice(formattedPrice);
      
      // Show price section if we have a valid price
      if (formattedPrice && formattedPrice !== '') {
        console.log('Setting showPriceSection to true because price is:', formattedPrice);
        setShowPriceSection(true);
      }
      
      // Set currency with fallback
      setCurrency(initialData.currency || 'ILS');
      
      // Format and handle date properly
      if (initialData.estimatedCompletionDate) {
        setEstimatedCompletionDate(initialData.estimatedCompletionDate);
        setShowDateSection(true);
      } else {
        setEstimatedCompletionDate('');
      }
      
      // Only update provider contact info if it's provided in initialData and is a string
      if (initialData.providerContactInfo && typeof initialData.providerContactInfo === 'string') {
        setProviderContactInfo(initialData.providerContactInfo);
        setShowContactSection(true);
      } else if (defaultContactInfo) {
        setProviderContactInfo(defaultContactInfo);
        setShowContactSection(true);
      }
    }
  }, [initialData, defaultContactInfo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (onSubmit) {
        await onSubmit({
          description,
          price,
          currency,
          estimatedCompletionDate,
          providerContactInfo,
          mediaFiles
        });
        return;
      }

      const offerData = {
        description,
        price: price ? parseFloat(price) : undefined,
        currency: currency || undefined,
        estimatedCompletionDate: estimatedCompletionDate || undefined,
        providerContactInfo
      };

      const files = mediaFiles;

      let response;
      
      if (isEditMode && offerId) {
        response = await updateOfferWithMedia(offerId, offerData, files);
      } else {
        response = await createOfferWithMedia(willId, offerData, files);
      }
      
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} offer:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} offer. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openMediaViewer = (index: number) => {
    setMediaViewerIndex(index);
    setShowMediaViewer(true);
  };

  const handleMediaChange = (files: any[]) => {
    setMediaFiles(Array.isArray(files) ? files : []);
    console.log("Media files selected:", files);
    
    if (Array.isArray(files)) {
      files.forEach((file, index) => {
        if (file.file) {
          console.log(`File ${index}:`, file.name, file.file.type, file.file.size);
        } else {
          console.log(`File ${index}:`, file);
        }
      });
    } else {
      console.log('No valid media files to upload');
    }
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      // Handle different date formats
      let date;
      
      // ISO string format (2023-04-01T12:00:00Z)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        date = new Date(dateString);
      } 
      // Unix timestamp (as number or string)
      else if (!isNaN(Number(dateString))) {
        date = new Date(Number(dateString));
      }
      // Simple date string (2023-04-01)
      else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date format:', dateString);
        return '';
      }
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="offer-form pb-8">
      {error && (
        <div className="error-message mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-start">
          <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>{error}</span>
        </div>
      )}
      
      {/* Description Field - Required */}
      <div className="form-group mb-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          <span className="mr-1">Description</span>
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            placeholder="Describe what you're offering in detail..."
            rows={5}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
        </div>
        <p className="text-gray-500 text-xs mt-1">Clearly explain what you'll provide, how it meets the requester's needs.</p>
      </div>
      
      {/* Price Section - Optional but Expandable */}
      <div className="mb-6 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowPriceSection(!showPriceSection)}
          className={`w-full flex items-center justify-between p-3 ${showPriceSection ? 'bg-blue-50 rounded-t-lg border-t border-x border-blue-200' : 'bg-gray-50 rounded-lg border border-gray-200'} text-left text-sm font-medium transition-all duration-200 hover:bg-blue-50`}
        >
          <div className="flex items-center">
            <CircleDollarSign className={`w-5 h-5 mr-2 ${showPriceSection ? 'text-blue-600' : 'text-gray-500'}`} />
            <span className={showPriceSection ? 'text-blue-700' : 'text-gray-700'}>Price</span>
          </div>
          {showPriceSection ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        
        {showPriceSection && (
          <div className="bg-white p-4 border-x border-b border-blue-200 rounded-b-lg shadow-sm">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                  Price
                </label>
                <div className="relative">
                  <input
                    id="price"
                    type="number"
                    value={price}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log('Price input changed to:', newValue);
                      // Only allow valid numbers
                      if (newValue === '' || !isNaN(Number(newValue))) {
                        setPrice(newValue);
                      }
                    }}
                    onBlur={(e) => {
                      // Format the price properly on blur
                      if (e.target.value !== '' && !isNaN(Number(e.target.value))) {
                        // Format with two decimal places if needed
                        const formattedValue = Number(e.target.value).toString();
                        setPrice(formattedValue);
                      }
                    }}
                    placeholder="Enter amount"
                    min="0"
                    step="0.01"
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">
                      {/* {currency === 'ILS' ? '₪' : 
                       currency === 'USD' ? '$' : 
                       currency === 'EUR' ? '€' : 
                       currency === 'GBP' ? '£' : '$'} */}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="ILS">₪ ILS - Israeli Shekel</option>
                  <option value="USD">$ USD - US Dollar</option>
                  <option value="EUR">€ EUR - Euro</option>
                  <option value="GBP">£ GBP - British Pound</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Estimated Completion Date - Optional but Expandable */}
      <div className="mb-6 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowDateSection(!showDateSection)}
          className={`w-full flex items-center justify-between p-3 ${showDateSection ? 'bg-blue-50 rounded-t-lg border-t border-x border-blue-200' : 'bg-gray-50 rounded-lg border border-gray-200'} text-left text-sm font-medium transition-all duration-200 hover:bg-blue-50`}
        >
          <div className="flex items-center">
            <CalendarIcon className={`w-5 h-5 mr-2 ${showDateSection ? 'text-blue-600' : 'text-gray-500'}`} />
            <span className={showDateSection ? 'text-blue-700' : 'text-gray-700'}>Estimated Completion Date</span>
          </div>
          {showDateSection ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        
        {showDateSection && (
          <div className="bg-white p-4 border-x border-b border-blue-200 rounded-b-lg shadow-sm">
            <div className="form-group">
              <label htmlFor="estimatedCompletionDate" className="block text-sm font-medium text-gray-700 mb-2">
                When do you expect to complete this?
              </label>
              <div className="relative">
                <input
                  id="estimatedCompletionDate"
                  type="date"
                  value={formatDateForInput(estimatedCompletionDate)}
                  onChange={(e) => setEstimatedCompletionDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  {/* <CalendarIcon className="w-5 h-5 text-gray-400" /> */}
                </div>
              </div>
              <p className="text-gray-500 text-xs mt-1">Providing a timeline helps set clear expectations</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Contact Information - Optional but Expandable */}
      <div className="mb-6 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowContactSection(!showContactSection)}
          className={`w-full flex items-center justify-between p-3 ${showContactSection ? 'bg-blue-50 rounded-t-lg border-t border-x border-blue-200' : 'bg-gray-50 rounded-lg border border-gray-200'} text-left text-sm font-medium transition-all duration-200 hover:bg-blue-50`}
        >
          <div className="flex items-center">
            <MessageSquare className={`w-5 h-5 mr-2 ${showContactSection ? 'text-blue-600' : 'text-gray-500'}`} />
            <span className={showContactSection ? 'text-blue-700' : 'text-gray-700'}>Contact Information</span>
          </div>
          {showContactSection ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        
        {showContactSection && (
          <div className="bg-white p-4 border-x border-b border-blue-200 rounded-b-lg shadow-sm">
            <div className="form-group">
              <label htmlFor="providerContactInfo" className="block text-sm font-medium text-gray-700 mb-2">
                How to contact you?
              </label>
              <Textarea
                id="providerContactInfo"
                value={providerContactInfo}
                onChange={(e) => setProviderContactInfo(e.target.value)}
                placeholder="Add your preferred contact method (email, phone, Telegram, etc.)"
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              <p className="text-gray-500 text-xs mt-1">This information will be shared with the wanter if your offer is accepted</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Want Attachments Section */}
      {willMedia && willMedia.length > 0 && (
        <div className="mb-6">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <PaperclipIcon className="w-4 h-4 mr-1 text-gray-500" />
              Want Attachments ({willMedia.length})
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {willMedia.map((media, index) => (
                <div 
                  key={`will-media-${index}`}
                  className="media-item group cursor-pointer rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200" 
                  onClick={() => openMediaViewer(index)}
                >
                  {media.type === 'image' ? (
                    <div className="relative h-24">
                      <img src={media.url} alt={media.name} className="w-full h-24 object-cover transition-transform duration-200 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="text-white text-xs font-medium py-1 px-2 bg-black bg-opacity-50 rounded">View</span>
                      </div>
                    </div>
                  ) : media.type === 'video' ? (
                    <div className="relative w-full h-24 bg-gray-900 flex items-center justify-center group-hover:bg-gray-800 transition-all duration-200">
                      <Video className="w-8 h-8 text-gray-300 opacity-70 group-hover:opacity-100 transition-all duration-200" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center group-hover:bg-white/40 transition-all duration-200">
                          <Play className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-24 bg-gray-100 flex flex-col items-center justify-center p-2 group-hover:bg-gray-200 transition-all duration-200">
                      <FileIcon className="w-8 h-8 text-gray-400 group-hover:text-gray-600 transition-all duration-200" />
                      <span className="text-xs mt-1 text-center text-gray-600 truncate max-w-full">
                        {media.name}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Your Attachments - Optional but Expandable */}
      <div className="mb-8 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAttachmentsSection(!showAttachmentsSection)}
          className={`w-full flex items-center justify-between p-3 ${showAttachmentsSection ? 'bg-blue-50 rounded-t-lg border-t border-x border-blue-200' : 'bg-gray-50 rounded-lg border border-gray-200'} text-left text-sm font-medium transition-all duration-200 hover:bg-blue-50`}
        >
          <div className="flex items-center">
            <PaperclipIcon className={`w-5 h-5 mr-2 ${showAttachmentsSection ? 'text-blue-600' : 'text-gray-500'}`} />
            <span className={showAttachmentsSection ? 'text-blue-700' : 'text-gray-700'}>Add Your Attachments</span>
          </div>
          {showAttachmentsSection ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        
        {showAttachmentsSection && (
          <div className="bg-white p-4 border-x border-b border-blue-200 rounded-b-lg shadow-sm">
            <div className="form-group">
              <MediaUploadForWill 
                onMediaChange={handleMediaChange}
                existingMedia={[]}
              />
              <p className="text-gray-500 text-xs mt-2">Upload images, documents, or other files to support your offer</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button 
          type="button" 
          onClick={onCancel} 
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-all duration-200"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-6 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 shadow-sm transition-all duration-200 flex items-center"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEditMode ? 'Updating...' : 'Submitting...'}
            </>
          ) : (
            isEditMode ? 'Update Offer' : 'Submit Offer'
          )}
        </button>
      </div>

      {showMediaViewer && (
        <MediaViewer
          media={willMedia}
          initialIndex={mediaViewerIndex}
          onClose={() => setShowMediaViewer(false)}
        />
      )}
    </form>
  );
};

export default OfferForm;
